import CopyableText from 'components/atoms/buttons/copyable-text';
import { Link } from 'react-router-dom';
import { Routes } from 'router/routes';
import { languagesMap } from 'constants/languages';
import {
  type CcmStatuses,
  DateFormats,
  type RpmStatuses,
  type Languages,
} from 'infrastructure/enums';
import { useMemo, type FC } from 'react';
import { Divider, Flex, Typography } from 'antd';
import UserAvatar from 'components/atoms/user-avatar';
import ProgramType from 'components/molecules/oneplaform/program-type';
import { DateFormatter } from 'infrastructure/functions';
import { useTranslation } from 'react-i18next';
import { CopyOutlined } from '@ant-design/icons';

import s from './styles.module.scss';

const { Text } = Typography;

interface PatientCellProps {
  name: string;
  guid: string;
  language: Languages;
  dob: string;
  provider: string;
  toggleOpen: () => void;
  rpmStatus?: RpmStatuses;
  ccmStatus?: CcmStatuses;
  isLoadingProgramType?: boolean;
}

const PatientCell: FC<PatientCellProps> = ({
  name,
  guid,
  toggleOpen,
  language,
  dob,
  provider,
  rpmStatus,
  ccmStatus,
  isLoadingProgramType,
}) => {
  const { t } = useTranslation();

  const { firstName, lastName } = useMemo(() => {
    const nameParts = name.split(' ');
    return {
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
    };
  }, [name]);

  const birthDate = useMemo(() => {
    return DateFormatter({
      date: dob || '',
      format: DateFormats['MM/dd/yyyy'],
    });
  }, [dob]);

  return (
    <Flex className={s['patient-cell-wrapper']} align="center">
      <Flex align="center" gap={12}>
        <UserAvatar
          firstName={firstName}
          lastName={lastName}
          size="large"
          className={s['avatar-button']}
        />

        <Flex vertical className={s['patient-info-wrapper']}>
          <Flex align="center" gap={12} wrap="wrap">
            <Text className={s.name}>
              <Link
                to={Routes.patientGuid(guid)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleOpen();
                }}
              >
                {name}
              </Link>
            </Text>
            <CopyableText
              className={s.guid}
              value={guid}
              label={t('labels.ptID')}
              icon={<CopyOutlined className={s['copy-icon']} />}
            />
            <ProgramType
              rpmStatus={rpmStatus}
              ccmStatus={ccmStatus}
              isLoading={isLoadingProgramType}
            />
          </Flex>

          <Flex align="center" gap={6}>
            <Text className={s.date}>{birthDate}</Text>
            <Divider type="vertical" className={s.divider} />
            <Text className={s.language}>{languagesMap[language]}</Text>
            <Divider type="vertical" className={s.divider} />
            <Text className={s.provider}>{provider}</Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
export default PatientCell;
